import React, { useState } from 'react';
import './NGODirectory.css';

interface NGO {
  id: number;
  name: string;
  city: string;
  mission: string;
  focus: string[];
  verified: boolean;
  image: string;
  description?: string;
  contact?: string;
  website?: string;
  achievements?: string[];
}

const NGODirectory: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [email, setEmail] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [selectedNGO, setSelectedNGO] = useState<NGO | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Sample NGO data
  const ngos: NGO[] = [
    {
      id: 1,
      name: "Pawsome Care Foundation",
      city: "Mumbai",
      mission: "Providing medical care and rehabilitation for injured street dogs",
      focus: ["Medical Care", "Rehabilitation", "Adoption"],
      verified: true,
      image: "🏥",
      description: "Founded in 2018, Pawsome Care Foundation has been at the forefront of street dog welfare in Mumbai. We operate a state-of-the-art medical facility and have successfully treated over 500 injured dogs.",
      contact: "+91 98765 43210",
      website: "www.pawsomecare.org",
      achievements: [
        "500+ dogs treated and rehabilitated",
        "15 successful adoption drives",
        "Partnership with 5 local veterinary clinics",
        "Community education programs in 10 neighborhoods"
      ]
    },
    {
      id: 2,
      name: "Street Dogs United",
      city: "Delhi",
      mission: "Feeding and vaccinating street dogs across Delhi neighborhoods",
      focus: ["Feeding", "Vaccination", "Community Education"],
      verified: true,
      image: "🍽️",
      description: "Street Dogs United works tirelessly to ensure no dog goes hungry in Delhi. Our daily feeding program reaches over 200 dogs across 15 neighborhoods.",
      contact: "+91 98765 43211",
      website: "www.streetdogsunited.org",
      achievements: [
        "Daily feeding of 200+ dogs",
        "Vaccination drives in 15 neighborhoods",
        "Community awareness workshops",
        "Partnership with local restaurants for food donations"
      ]
    },
    {
      id: 3,
      name: "Happy Tails Rescue",
      city: "Bangalore",
      mission: "Rescuing and rehoming abandoned dogs with loving families",
      focus: ["Rescue", "Adoption", "Foster Care"],
      verified: true,
      image: "🏠",
      description: "Happy Tails Rescue specializes in finding forever homes for abandoned and surrendered dogs. Our foster network spans across Bangalore.",
      contact: "+91 98765 43212",
      website: "www.happytailsrescue.org",
      achievements: [
        "300+ successful adoptions",
        "Active foster network of 50 families",
        "Behavioral training programs",
        "Post-adoption support and follow-ups"
      ]
    },
    {
      id: 4,
      name: "Compassionate Canines",
      city: "Chennai",
      mission: "Building rabies-free zones through vaccination drives",
      focus: ["Vaccination", "Sterilization", "Community Outreach"],
      verified: false,
      image: "💉",
      description: "Compassionate Canines focuses on creating rabies-free zones through systematic vaccination and sterilization programs.",
      contact: "+91 98765 43213",
      website: "www.compassionatecanines.org",
      achievements: [
        "Vaccinated 1000+ dogs",
        "Created 3 rabies-free zones",
        "Community education programs",
        "Partnership with local government"
      ]
    }
  ];

  const cities = Array.from(new Set(ngos.map(ngo => ngo.city)));

  const filteredNGOs = ngos.filter(ngo => {
    const matchesSearch = ngo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ngo.mission.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCity = selectedCity === '' || ngo.city === selectedCity;
    return matchesSearch && matchesCity;
  });

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the email to your backend
    alert(`Thank you! We'll send the full directory to ${email}`);
    setShowEmailForm(false);
    setEmail('');
  };

  const handleViewProfile = (ngo: NGO) => {
    setSelectedNGO(ngo);
    setShowProfileModal(true);
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedNGO(null);
  };

  return (
    <div className="ngo-directory">
      <div className="container">
        <div className="page-header">
          <h1>🐾 NGO Directory</h1>
          <p>Connect with verified dog welfare organizations across India</p>
        </div>

        {/* Search and Filter Section */}
        <div className="search-section">
          <div className="search-filters">
            <input
              type="text"
              placeholder="Search NGOs by name or mission..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="city-filter"
            >
              <option value="">All Cities</option>
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
          
          <button 
            className="btn btn-primary"
            onClick={() => setShowEmailForm(true)}
          >
            Get Full Directory
          </button>
        </div>

        {/* Email Form Modal */}
        {showEmailForm && (
          <div className="email-modal">
            <div className="email-modal-content">
              <h3>Get the Complete NGO Directory</h3>
              <p>Enter your email to receive our comprehensive directory with contact details and donation links.</p>
              <form onSubmit={handleEmailSubmit}>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="email-input"
                />
                <div className="email-buttons">
                  <button type="submit" className="btn btn-primary">
                    Send Directory
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowEmailForm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* NGO Profile Modal */}
        {showProfileModal && selectedNGO && (
          <div className="profile-modal">
            <div className="profile-modal-content">
              <div className="profile-header">
                <div className="profile-image">
                  {selectedNGO.image}
                </div>
                <div className="profile-info">
                  <h2>{selectedNGO.name}</h2>
                  <p className="profile-city">📍 {selectedNGO.city}</p>
                  {selectedNGO.verified && (
                    <span className="verified-badge">✓ Verified</span>
                  )}
                </div>
                <button className="close-button" onClick={closeProfileModal}>
                  ✕
                </button>
              </div>
              
              <div className="profile-body">
                <div className="profile-section">
                  <h3>Mission</h3>
                  <p>{selectedNGO.mission}</p>
                </div>
                
                {selectedNGO.description && (
                  <div className="profile-section">
                    <h3>About Us</h3>
                    <p>{selectedNGO.description}</p>
                  </div>
                )}
                
                <div className="profile-section">
                  <h3>Focus Areas</h3>
                  <div className="focus-tags">
                    {selectedNGO.focus.map(focus => (
                      <span key={focus} className="focus-tag">{focus}</span>
                    ))}
                  </div>
                </div>
                
                {selectedNGO.achievements && (
                  <div className="profile-section">
                    <h3>Key Achievements</h3>
                    <ul className="achievements-list">
                      {selectedNGO.achievements.map((achievement, index) => (
                        <li key={index}>{achievement}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="profile-section">
                  <h3>Contact Information</h3>
                  <div className="contact-info">
                    {selectedNGO.contact && (
                      <div className="contact-item">
                        <span className="contact-icon">📞</span>
                        <span>{selectedNGO.contact}</span>
                      </div>
                    )}
                    {selectedNGO.website && (
                      <div className="contact-item">
                        <span className="contact-icon">🌐</span>
                        <a href={`https://${selectedNGO.website}`} target="_blank" rel="noopener noreferrer">
                          {selectedNGO.website}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="profile-actions">
                <button className="btn btn-primary">Contact NGO</button>
                <button className="btn btn-secondary">Donate Now</button>
              </div>
            </div>
          </div>
        )}

        {/* NGO Listings */}
        <div className="ngo-grid">
          {filteredNGOs.map(ngo => (
            <div key={ngo.id} className="ngo-card">
              <div className="ngo-header">
                <div className="ngo-image">
                  {ngo.image}
                </div>
                <div className="ngo-info">
                  <h3>{ngo.name}</h3>
                  <p className="ngo-city">📍 {ngo.city}</p>
                  {ngo.verified && (
                    <span className="verified-badge">✓ Verified</span>
                  )}
                </div>
              </div>
              
              <p className="ngo-mission">{ngo.mission}</p>
              
              <div className="ngo-focus">
                <h4>Focus Areas:</h4>
                <div className="focus-tags">
                  {ngo.focus.map(focus => (
                    <span key={focus} className="focus-tag">{focus}</span>
                  ))}
                </div>
              </div>
              
              <div className="ngo-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => handleViewProfile(ngo)}
                >
                  View Profile
                </button>
                <button className="btn btn-secondary">Donate</button>
              </div>
            </div>
          ))}
        </div>

        {filteredNGOs.length === 0 && (
          <div className="no-results">
            <h3>No NGOs found</h3>
            <p>Try adjusting your search terms or city filter.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NGODirectory; 